import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Font,
} from '@react-pdf/renderer';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/context/AuthContext.tsx';

interface OrderCustomer {
  externalId: string;
  companyName?: string | null;
  city?: string | null;
  address?: string | null;
  postCode?: string | null;
  country?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

interface OrderCreditPackage {
  externalId: string;
  name: string;
  credits: number;
  price: number;
}

interface OrderSeller {
  companyName?: string | null;
  address?: string | null;
  city?: string | null;
  postCode?: string | null;
  country?: string | null;
}

interface OrderDetailsState {
  externalId: string;
  status: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  customer?: OrderCustomer;
  creditPackage?: OrderCreditPackage;
  seller?: OrderSeller;
  createdAt: string;
  updatedAt: string;
}

const formatCurrency = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const renderCustomerName = (customer?: OrderCustomer) => {
  if (!customer) return '—';
  const first = customer.firstName?.trim() || '';
  const last = customer.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || customer.companyName || '—';
};

// Register font with Cyrillic support - must be done outside component
// Using Roboto from CDN which supports Cyrillic, Latin, and other scripts
// react-pdf only supports TTF format
// IMPORTANT: If CDN doesn't work due to CORS, download TTF files locally to /public/fonts/ and use:
// src: '/fonts/roboto-regular.ttf'
try {
  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      },
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
        fontWeight: 700,
      },
    ],
  });
} catch (e) {
  // Font may already be registered or CDN failed
  console.warn('Font registration issue - consider using local TTF files:', e);
}

const OrderDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams<{ orderId: string }>();
  const order = location.state as OrderDetailsState | undefined;
  const [downloading, setDownloading] = useState(false);

  const { user } = useAuth();

  const getSellerInfo = (o: OrderDetailsState | undefined) => {
    if (!o?.seller)
      return { companyName: ' ', addresses: ' ', city: ' ', country: ' ' };
    const s = o.seller;
    const addresses = [s.address, s.postCode].filter(Boolean).join(', ') || ' ';
    return {
      companyName: s.companyName || ' ',
      addresses,
      city: s.city || ' ',
      country: s.country || ' ',
    };
  };

  const formatDateForInvoice = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatCurrencyForTable = (amount: number) => {
    try {
      const formatted = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      return `${formatted}€`;
    } catch (_error) {
      return `${amount.toFixed(2)}€`;
    }
  };

  const pdfStyles = StyleSheet.create({
    page: {
      padding: 40,
      fontSize: 11,
      fontFamily: 'Roboto',
      color: '#000000',
    },
    logoContainer: {
      marginBottom: 20,
      alignItems: 'flex-start',
    },
    logo: {
      width: 120,
      height: 60,
      objectFit: 'contain',
    },
    header: {
      marginBottom: 30,
    },
    invoiceTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 8,
      textTransform: 'uppercase',
    },
    companyTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 20,
    },
    dateRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 30,
      fontSize: 11,
    },
    twoColumns: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 30,
    },
    leftColumn: {
      width: '48%',
    },
    rightColumn: {
      width: '48%',
    },
    addressLine: {
      marginBottom: 4,
      fontSize: 11,
    },
    addressTitle: {
      fontWeight: 'bold',
      marginBottom: 8,
      fontSize: 12,
    },
    greeting: {
      marginBottom: 20,
      fontSize: 11,
      lineHeight: 1.5,
    },
    table: {
      marginBottom: 20,
      borderWidth: 1,
      borderColor: '#000000',
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#000000',
    },
    tableHeader: {
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
    },
    tableCell: {
      padding: 8,
      fontSize: 10,
      borderRightWidth: 1,
      borderRightColor: '#000000',
    },
    tableCellLast: {
      padding: 8,
      fontSize: 10,
    },
    tableCellPos: {
      width: '8%',
      textAlign: 'center',
    },
    tableCellDesc: {
      width: '32%',
    },
    tableCellQty: {
      width: '12%',
      textAlign: 'center',
    },
    tableCellUnit: {
      width: '12%',
      textAlign: 'center',
    },
    tableCellPrice: {
      width: '18%',
      textAlign: 'right',
    },
    tableCellTotal: {
      width: '18%',
      textAlign: 'right',
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 8,
      fontSize: 11,
    },
    summaryLabel: {
      fontWeight: 'bold',
    },
    summaryValue: {
      fontWeight: 'bold',
    },
    footer: {
      marginTop: 40,
      fontSize: 10,
      lineHeight: 1.6,
    },
    footerLine: {
      marginBottom: 4,
    },
  });

  const OrderDocument: React.FC<{ t: (key: string) => string }> = ({ t }) => {
    const sellerInfo = getSellerInfo(order);

    if (!order) return null;

    const customerAddress =
      [
        order.customer?.address,
        order.customer?.postCode,
        order.customer?.city,
        order.customer?.country,
      ]
        .filter(Boolean)
        .join(', ') || '—';

    const subtotal = order.amount;
    const grandTotal = subtotal;

    return (
      <Document>
        <Page size="A4" style={pdfStyles.page}>
          {/* Header with Logo */}
          <View style={pdfStyles.header}>
            <Text style={pdfStyles.invoiceTitle}>
              {t('orderDetails.pdf.invoice')}
            </Text>
            <View style={pdfStyles.dateRow}>
              <Text>
                {t('orderDetails.pdf.date')}:{' '}
                {formatDateForInvoice(order.createdAt)}
              </Text>
              <Text>
                {t('orderDetails.pdf.invoiceNumber')}:{' '}
                {order.externalId || orderId}
              </Text>
            </View>
          </View>

          {/* Two Columns - Company and Customer */}
          <View style={pdfStyles.twoColumns}>
            <View style={pdfStyles.leftColumn}>
              <Text style={pdfStyles.addressTitle}>
                {sellerInfo.companyName}
              </Text>
              <Text style={pdfStyles.addressLine}>{sellerInfo.addresses}</Text>
              <Text style={pdfStyles.addressLine}>
                {sellerInfo.city}, {sellerInfo.country}
              </Text>
            </View>
            <View style={pdfStyles.rightColumn}>
              <Text style={pdfStyles.addressLine}>
                {user?.customer?.customerType?.name === 'Business'
                  ? order.customer?.companyName
                  : renderCustomerName(order.customer)}
              </Text>
              <Text style={pdfStyles.addressLine}>{customerAddress}</Text>
              {order.customer?.email && (
                <Text style={pdfStyles.addressLine}>
                  Email: {order.customer.email}
                </Text>
              )}
            </View>
          </View>

          {/* Greeting */}
          <View style={pdfStyles.greeting}>
            <Text>{t('orderDetails.pdf.greeting')}</Text>
            <Text>{t('orderDetails.pdf.greetingText1')}</Text>
            <Text>{t('orderDetails.pdf.greetingText2')}</Text>
          </View>

          {/* Table */}
          <View style={pdfStyles.table}>
            {/* Table Header */}
            <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPos]}>
                {t('orderDetails.pdf.table.position')}
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellDesc]}>
                {t('orderDetails.pdf.table.description')}
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellQty]}>
                {t('orderDetails.pdf.table.quantity')}
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellUnit]}>
                {t('orderDetails.pdf.table.unit')}
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPrice]}>
                {t('orderDetails.pdf.table.unitPrice')}
              </Text>
              <Text style={[pdfStyles.tableCellLast, pdfStyles.tableCellTotal]}>
                {t('orderDetails.pdf.table.total')}
              </Text>
            </View>
            {/* Table Row */}
            <View style={pdfStyles.tableRow}>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPos]}>
                1.
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellDesc]}>
                {order.creditPackage?.name ||
                  t('orderDetails.pdf.defaultProductName')}
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellQty]}>
                1
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellUnit]}>
                {t('orderDetails.pdf.table.piece')}
              </Text>
              <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPrice]}>
                {formatCurrencyForTable(
                  order.amount,
                )}
              </Text>
              <Text style={[pdfStyles.tableCellLast, pdfStyles.tableCellTotal]}>
                {formatCurrencyForTable(order.amount)}
              </Text>
            </View>
          </View>

          {/* Summary */}
          <View>
            <View style={pdfStyles.summaryRow}>
              <Text style={pdfStyles.summaryLabel}>
                {t('orderDetails.pdf.subtotal')}
              </Text>
              <Text style={pdfStyles.summaryValue}>
                {formatCurrencyForTable(subtotal)}
              </Text>
            </View>
            <View style={pdfStyles.summaryRow}>
              <Text style={pdfStyles.summaryLabel}>
                {t('orderDetails.pdf.totalAmount')}
              </Text>
              <Text style={pdfStyles.summaryValue}>
                {formatCurrencyForTable(grandTotal)}
              </Text>
            </View>
          </View>

          {/* Footer */}
          <View style={pdfStyles.footer}>
            <Text style={pdfStyles.footerLine}>
              {t('orderDetails.pdf.closing')}
            </Text>
            <Text style={pdfStyles.footerLine}>{sellerInfo.companyName}</Text>
            <Text style={pdfStyles.footerLine}>{sellerInfo.addresses}</Text>
            <Text style={pdfStyles.footerLine}>
              {sellerInfo.city}, {sellerInfo.country}
            </Text>
            <Text style={[pdfStyles.footerLine, { marginTop: 12 }]}>
              {t('orderDetails.pdf.invoiceNumber')}:{' '}
              {order.externalId || orderId}
            </Text>
          </View>
        </Page>
      </Document>
    );
  };

  const handleDownload = async () => {
    if (!order) return;
    try {
      setDownloading(true);
      const blob = await pdf(<OrderDocument t={t} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-${order.externalId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate order PDF', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {t('orderDetails.orderDetailsUnavailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('orderDetails.couldNotLoadDetails')}
            </Typography>
            <Button
              sx={{ mt: 3 }}
              variant="contained"
              onClick={() => navigate('/order-history')}
            >
              {t('orderDetails.backToOrderHistory')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const sellerInfo = getSellerInfo(order);

  const customerAddress =
    [
      order.customer?.address,
      order.customer?.postCode,
      order.customer?.city,
      order.customer?.country,
    ]
      .filter(Boolean)
      .join(', ') || '—';

  const subtotal = order.amount;
  const vat = 0; // 0% VAT
  const grandTotal = subtotal;

  return (
    <Box sx={{ p: 3, mx: 'auto' }}>
      {/* Header with back button and download */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Button variant="outlined" onClick={() => navigate('/order-history')}>
          {t('orderDetails.backToHistory')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          disabled={downloading}
          startIcon={downloading ? <CircularProgress size={18} /> : undefined}
        >
          {downloading
            ? t('orderDetails.generating')
            : t('orderDetails.downloadPdf')}
        </Button>
      </Stack>

      {/* Invoice Container */}
      <Paper sx={{ p: 4 }} elevation={2}>
        {/* Header Section - Logo and Seller Info */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-start"
          sx={{ mb: 4 }}
        >
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {sellerInfo.companyName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sellerInfo.addresses}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sellerInfo.city}, {sellerInfo.country}
            </Typography>
          </Box>
        </Stack>

        {/* Invoice Details and Sold To Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }} variant="outlined">
              <Typography variant="h6" fontWeight={600} gutterBottom>
                INVOICE
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Invoice No:
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {order.externalId || orderId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Date:
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDate(order.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Sold To:
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body1" fontWeight={600}>
                  {user?.customer?.customerType?.name === 'Business'
                    ? order.customer?.companyName
                    : renderCustomerName(order.customer)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {customerAddress}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Line Items Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    #
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Order
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={600}>
                    Unit Price
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={600}>
                    Quantity
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={600}>
                    Amount
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>1</TableCell>
                <TableCell>
                  {order.creditPackage?.name || 'Software Development Credits'}
                </TableCell>
                <TableCell align="right">
                  {order.creditPackage
                    ? formatCurrency(order.amount, order.currency)
                    : formatCurrency(order.amount, order.currency)}
                </TableCell>
                <TableCell align="right">1</TableCell>
                <TableCell align="right">
                  {formatCurrency(order.amount, order.currency)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Totals */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Paper sx={{ p: 2, minWidth: 300 }} variant="outlined">
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(subtotal, order.currency)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">VAT (0%):</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(vat, order.currency)}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                  Grand Total:
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(grandTotal, order.currency)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default OrderDetailsPage;
